import { z } from 'zod';
import { unitSchema } from '../catalog/unit.js';
import { unknownUnitSchema } from '../catalog/unit-alias.schema.js';
/**
 * 1C MODDIY HISOBOTI — buxgalterdan skladchiga keladigan hujjat.
 *
 * OQIM: mahsulot omborga keladi → buxgalter 1C dan chiqqan hisobotni
 * yuklaydi → hujjat skladchining navbatiga tushadi → skladchi tasdiqlab
 * stellajlarga joylashtiradi.
 *
 * MARKAZIY QOIDA: hisobotdagi `Остаток` — omborda TURISHI KERAK bo'lgan
 * miqdor, "yangi kelgani" emas. Har yetkazmada to'liq surat kelgani uchun
 * joylashtiriladigan miqdor AYIRMA bilan hisoblanadi:
 * `balanceQty − omborda hozir turgani`. Aks holda ikkinchi fayl butun
 * omborni ikkilantirib yuborardi.
 */
/**
 * HUJJAT HOLATI.
 *
 * `DRAFT` — buxgalter yukladi, LEKIN hali yubormadi. Bu holat ataylab
 * kiritilgan: 1C dan chiqqan fayl goho noto'g'ri ombor yoki noto'g'ri davr
 * bilan chiqadi va xato faqat satrlarni ko'rgandan keyin bilinadi.
 * Qoralama skladchiga KO'RINMAYDI, unga xabar ketmaydi va uni butunlay
 * o'chirib tashlash mumkin. `SENT` bo'lgandan keyin esa hujjat boshqa
 * odamning ish ro'yxatida turadi — u endi o'chirilmaydi, faqat bekor
 * qilinadi.
 */
export const SUPPLY_REPORT_STATUSES = [
    'DRAFT',
    'SENT',
    'PARTIAL',
    'ACCEPTED',
    'CANCELLED',
];
export const supplyReportStatusSchema = z.enum(SUPPLY_REPORT_STATUSES);
const STATUS_LABELS = {
    DRAFT: 'Qoralama',
    SENT: 'Navbatda',
    PARTIAL: 'Qisman joylashtirilgan',
    ACCEPTED: 'Joylashtirilgan',
    CANCELLED: 'Bekor qilingan',
};
export const supplyReportStatusLabel = (status) => STATUS_LABELS[status];
/**
 * Hisobotdagi bitta satr.
 *
 * `price` va `account` — BUXGALTERIYA ma'lumoti. Ular javobda faqat
 * `accounting.doc.read` ruxsati bo'lganda to'ladi; skladchi uchun `null`
 * bo'lib keladi va uning ekranida umuman ko'rinmaydi.
 */
export const supplyReportLineSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    /** Fayldagi HAQIQIY qator raqami — odam xatoni faylda topadi. */
    sheetRow: z.number().int(),
    code: z.string(),
    name: z.string(),
    /** 1C ning O'Z yozuvi: `шт (упаковка)`. Rasmiy qog'ozda shu chiqadi. */
    unitRaw: z.string(),
    unit: unitSchema,
    balanceQty: z.number(),
    placedQty: z.number(),
    /** `balanceQty − omborda turgani`. Nol bo'lsa satr ish ro'yxatiga tushmaydi. */
    toPlaceQty: z.number(),
    nomenclatureId: z.string().nullable(),
    /**
     * «Поставщик» — SATRNING o'z ma'lumoti: bitta hisobotda o'nlab
     * ta'minotchi bo'ladi. Bo'sh bo'lishi mumkin, o'shanda skladchi
     * joylashtirayotganda o'zi yozadi.
     */
    supplier: z.string().nullable(),
    supplierInn: z.string().nullable(),
    /** «Дата поступления» — `YYYY-MM-DD`, kalendar kuni. */
    arrivedAt: z.string().nullable(),
    account: z.string().nullable(),
    price: z.number().nullable(),
});
export const supplyReportSchema = z.object({
    id: z.string(),
    docNo: z.string(),
    warehouseId: z.string(),
    periodLabel: z.string().nullable(),
    sourceLabel: z.string().nullable(),
    status: supplyReportStatusSchema,
    uploadedBy: z.string().nullable(),
    acceptedBy: z.string().nullable(),
    acceptedAt: z.string().nullable(),
    lineCount: z.number().int(),
    /**
     * ISH BOR satrlar soni — kartochkadagi `12 / 188` ning MAXRAJI.
     *
     * `lineCount` dan farqi: 1C hisobotida qoldig'i NOL bo'lgan satrlar
     * ham keladi (mahsulot katalogda bor, lekin omborda yo'q). Ularda
     * joylashtiradigan hech narsa yo'q va ular ishning o'lchoviga
     * kirmasligi kerak — aks holda hujjat hech qachon `188/190` dan
     * nariga o'tmasdi va ikkita satr abadiy "qolgan" bo'lib turardi.
     *
     * Hammasi qoldiqli hujjatda bu son `lineCount` ga TENG — eski
     * hujjatlarda ko'rinish o'zgarmaydi.
     */
    workLineCount: z.number().int(),
    /**
     * TO'LIQ joylashtirilgan satrlar soni — kartochkadagi `12 / 188`.
     *
     * Yarim joylashtirilgan satr "tugagan" deb sanalmaydi: son QOLGAN
     * ishni ko'rsatishi kerak.
     *
     * Qoldig'i NOL bo'lgan satr ham sanalmaydi. `placed_qty >= balance_qty`
     * sharti `0 >= 0` da ham rost bo'ladi va shunday satr JOYLASHTIRILGAN
     * bo'lib ko'rinardi — hech kim unga tegmagan bo'lsa ham.
     */
    placedLineCount: z.number().int(),
    /**
     * BOSHLANGAN, lekin tugamagan satrlar soni.
     *
     * `placedLineCount` dan tashqarida: bu satrlarga tegilgan, lekin
     * qoldiqning hammasi javonga chiqmagan (`0 < placed_qty < balance_qty`).
     *
     * Nega kerak: ilgari kartochkada faqat TUGAGAN satrlar ko'rinardi va
     * omborchi o'ttizta satrning yarmini joylashtirganda chiziq umuman
     * qimirlamasdi — «joylashtirdim-ku, nega to'lamayapti» degan savol
     * aynan shundan chiqardi. Endi boshlangan ish ham ko'rinadi, lekin
     * TUGAGAN bilan qo'shilib ketmaydi: hujjat qachon yopilishini baribir
     * `placedLineCount` hal qiladi.
     */
    startedLineCount: z.number().int(),
    /** Nechta ta'minotchi — joylashtirish shuncha kirimga bo'linadi. */
    supplierCount: z.number().int(),
    note: z.string().nullable(),
    createdAt: z.string(),
});
export const supplyReportWithLinesSchema = supplyReportSchema.extend({
    lines: z.array(supplyReportLineSchema),
});
/** Faylni o'qishda topilgan xato — qaysi qatorda va nima. */
export const supplySheetErrorSchema = z.object({
    sheetRow: z.number().int(),
    message: z.string(),
});
/**
 * KO'RIB CHIQISH javobi — bazaga hech narsa yozilmaydi.
 *
 * Buxgalter faylni yuborishdan OLDIN nima bo'lishini ko'radi: nechta satr
 * o'qildi, nechtasi yangi material, qayerda xato bor. Ko'rmasdan yuborilgan
 * fayl keyin qo'lda tuzatiladigan 200 ta satrga aylanardi.
 */
/**
 * FAYL QANDAY TUSHUNILGANI — xato bo'lsa ham, bo'lmasa ham.
 *
 * «Sarlavha topilmadi» xabari to'g'ri, lekin foydasiz: odam faylni
 * ochib, nimasi yoqmaganini taxmin qilishga majbur. Shuning uchun
 * parser KO'RGANINI ham qaytaradi va ekran uni ko'rsatadi — qaysi
 * qator sarlavha bo'ldi, qaysi ustun nima deb olindi, faylda umuman
 * qanday yorliqlar bor. Shundan keyin sabab ekranda ko'rinadi.
 */
export const sheetShapeSchema = z.object({
    /** `REPORT` — moddiy hisobot, `BALANCE` — ombor qoldig'i, `UNKNOWN` — tanilmadi. */
    kind: z.enum(['REPORT', 'BALANCE', 'UNKNOWN']),
    /** Sarlavha qatori Excel raqami bilan. Topilmasa `null`. */
    headerRow: z.number().int().nullable(),
    /** Tanilgan ustunlar: maydon va fayldagi AYNAN o'sha yorliq. */
    columns: z.array(z.object({ field: z.string(), label: z.string() })),
    /** Faylda ko'rilgan yorliqlar — tanilmaganda solishtirish uchun. */
    seen: z.array(z.string()),
});
export const supplyPreviewSchema = z.object({
    shape: sheetShapeSchema,
    periodLabel: z.string().nullable(),
    sourceLabel: z.string().nullable(),
    lineCount: z.number().int(),
    /** Katalogda hali yo'q kodlar — tasdiqlashda O'ZI yaratiladi. */
    newCodeCount: z.number().int(),
    totalBalanceQty: z.number(),
    /** Fayldagi «Итого» — yuqoridagi yig'indi bilan tushmasa, ogohlantiriladi. */
    sheetTotalQty: z.number().nullable(),
    errors: z.array(supplySheetErrorSchema),
    /** Yuborishni TO'SMAYDIGAN eslatmalar — masalan, 1C da kodi yo'q satrga kod berildi. */
    warnings: z.array(supplySheetErrorSchema),
    /**
     * TANILMAGAN birliklar — xato ro'yxatidan ALOHIDA.
     *
     * Sabab: qolgan xatolar faylning o'zida tuzatiladi (1C ga kirib,
     * tuzatib, qaytadan eksport qilinadi), bu esa TIZIMDA tuzatiladi —
     * odam «дона(кадок) bu dona ekan» deb bir marta aytadi va boshqa
     * hech qachon so'ralmaydi. Ikkisi bir ro'yxatda tursa, hal qilish
     * yo'li ham bir xildek ko'rinardi.
     */
    unknownUnits: z.array(unknownUnitSchema),
    /**
     * Xuddi SHU fayl allaqachon yuklangan bo'lsa — o'sha hujjat.
     *
     * Bir faylni ikki marta yuklash haqiqiy xato: ikkinchi hujjat o'sha
     * satrlarni qayta taklif qiladi va bir partiya ikki marta kataklarga
     * tushib ketishi mumkin. Fayl BAYTLARI bo'yicha topiladi — nom
     * o'zgargani bilan mazmuni o'zgarmaydi.
     */
    duplicateOf: z
        .object({ id: z.string(), docNo: z.string(), createdAt: z.string() })
        .nullable(),
    lines: z.array(z.object({
        sheetRow: z.number().int(),
        code: z.string(),
        name: z.string(),
        unitRaw: z.string(),
        unit: unitSchema,
        balanceQty: z.number(),
        isNew: z.boolean(),
        account: z.string().nullable(),
        price: z.number().nullable(),
    })),
});
/** Yuklash so'rovining MATN maydonlari — satrlar faylning ichida keladi. */
export const createSupplyReportSchema = z.object({
    warehouseId: z.string().regex(/^\d+$/),
    note: z.string().max(500).optional(),
});
/** Bitta satrni bitta katakka joylashtirish. */
export const supplyPlacementSchema = z.object({
    lineId: z.string().regex(/^\d+$/),
    cellId: z.string().regex(/^\d+$/),
    qty: z.number().positive().max(1_000_000_000),
    /**
     * Ta'minotchi — FAQAT satrniki bo'sh bo'lganda.
     *
     * Hisobotda ta'minotchisi ko'rsatilmagan satrlar bor (haqiqiy faylda
     * 233 tadan 27 tasi) va ularni skladchi joylashtirayotganda to'ldiradi.
     * Satrda ta'minotchi TURGAN bo'lsa bu maydon e'tiborga olinmaydi:
     * hujjatdagi yozuvni ekrandan o'zgartirib bo'lmasligi kerak.
     */
    supplier: z.string().trim().min(2).max(200).optional(),
});
/**
 * Joylashtirish so'rovi.
 *
 * Bitta satr bir NECHTA katakka bo'linishi mumkin (300 dona blank bitta
 * katakka sig'maydi), shuning uchun ro'yxat satr emas, JOYLASHTIRISH
 * ro'yxati: bir `lineId` bir necha marta uchrashi mumkin.
 */
export const placeSupplySchema = z.object({
    placements: z.array(supplyPlacementSchema).min(1).max(500),
});
/**
 * Joylashtirish natijasi.
 *
 * `docNos` — KO'PLIK. Joylashtirilganlar TA'MINOTCHI bo'yicha guruhlanadi
 * va har guruhga o'z kirim hujjati yoziladi: bitta hisobotda o'nlab
 * ta'minotchi bo'ladi, kirim hujjatining `Ta'minotchi` maydoni esa bitta.
 * Aralash hujjat yozilsa o'sha maydon ma'nosini yo'qotardi.
 */
/**
 * Joylashtirishda TUG'ILGAN partiya.
 *
 * Javobda ataylab: har joylashtirilgan satr yangi partiya yaratadi va
 * uning etiketkasi O'SHA PAYT qutiga yopishtirilishi kerak. Ilgari javob
 * faqat hujjat raqamini qaytarardi va skladchi etiketka uchun qoldiq
 * qidiruviga o'tib, partiyani qayta topishga majbur edi — ya'ni
 * etiketkasiz quti javonga chiqib ketishining eng oson yo'li shu edi.
 */
export const placedBatchSchema = z.object({
    batchId: z.string(),
    batchNo: z.string(),
    nomenclatureName: z.string(),
    unit: unitSchema,
    qty: z.number(),
    cellCode: z.string(),
});
export const placeSupplyResultSchema = z.object({
    docNos: z.array(z.string()),
    placedLines: z.number().int(),
    status: supplyReportStatusSchema,
    batches: z.array(placedBatchSchema),
});
//# sourceMappingURL=supply.schema.js.map